// src/pages/EditBankAccount.tsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { apiGet, apiPut } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

// Root E: the stored opening-balance scalar is deliberately absent from this
// type. The API still returns it, but this component must never read it -- the
// displayed "Balance" is the computed ledger balance, and that scalar is
// immutable after creation (Root D). Omitting it here makes it structurally
// impossible to reintroduce a read or to echo it back on save.
type Account = {
  id: number;
  name: string;
  type: string;
  kind: string;
};

type TrialBalanceRow = {
  account_id: number;
  balance: number;
};

type TrialBalancePayload = { rows?: TrialBalanceRow[] } | TrialBalanceRow[];

// Read-only balance state, kept strictly OUTSIDE the editable form so a
// computed balance can never be submitted as the stored opening scalar.
type BalanceState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "missing" }
  | { status: "ready"; value: number };

const UNAVAILABLE = "—";
const PENDING = "…";

// Correct sign placement for money: -$120.00, never $-120.00. Defined locally
// (and identically in BankAccountList.tsx) because Root E's authorized scope is
// these two components; the shared formatNumber/formatMoney helpers place the
// currency symbol before the minus sign.
function formatSignedMoney(n: number): string {
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

type SaveError = { kind: "server"; detail: string } | { kind: "fallback" };

export default function EditBankAccount() {
  const { t } = useTranslation();
  const { id, company_id } = useParams<{ id: string; company_id: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<{
    name: string;
    type: string;
    kind: string;
  }>({
    name: "",
    type: "Asset",
    kind: "bank",
  });

  const [balance, setBalance] = useState<BalanceState>({ status: "loading" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<SaveError | null>(null);

  useEffect(() => {
    if (!id) return;
    apiGet<Account>(`/accounts/${id}`).then((res) => {
      setForm({
        name: res.name ?? "",
        type: res.type ?? "Asset",
        kind: res.kind ?? "bank",
      });
    });
  }, [id]);

  // Root E: a SEPARATE effect from the account load, never gated behind a
  // shared Promise.all -- a slow, failing or forbidden balance request must not
  // block the editable form.
  //
  // No as-of date parameter is sent; the server's own date.today() cutoff
  // applies, matching every other accounting surface.
  useEffect(() => {
    if (!id) return;
    let alive = true;
    setBalance({ status: "loading" });

    apiGet<TrialBalancePayload>("/reports/trial-balance")
      .then((payload) => {
        if (!alive) return;
        const rows = Array.isArray(payload) ? payload : payload?.rows ?? [];
        const row = (rows || []).find(
          (r) => Number(r?.account_id) === Number(id),
        );
        if (
          row &&
          typeof row.balance === "number" &&
          !Number.isNaN(row.balance)
        ) {
          setBalance({ status: "ready", value: row.balance });
        } else {
          setBalance({ status: "missing" });
        }
      })
      .catch(() => {
        if (alive) setBalance({ status: "error" });
      });

    return () => {
      alive = false;
    };
  }, [id]);

  const balanceText = (): string => {
    if (balance.status === "loading") return PENDING;
    if (balance.status === "ready") return formatSignedMoney(balance.value);
    // "error" (any non-2xx, including the company-admin 403) and "missing"
    // both render the unavailable placeholder -- never 0, never a stored scalar.
    return UNAVAILABLE;
  };

  const labelCls = `block mb-1 ${A.label}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    setError(null);

    try {
      // Root E: the stored opening-balance scalar is omitted entirely. Root D
      // preserves an omitted value (exclude_unset), so it stays untouched, and
      // the computed balance can never be echoed back into it.
      await apiPut(`/accounts/${id}`, {
        name: form.name.trim(),
        type: form.type,
        kind: form.kind,
      });
      navigate(companyRoute(company_id, "/settings/accounts"));
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(detail ? { kind: "server", detail } : { kind: "fallback" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4 sm:pt-6`}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-md flex items-center border-b border-gray-100 px-2 pb-2 mb-4">
        <button
          onClick={() => navigate(-1)}
          className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.back")}
          type="button"
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <h2 className={`flex-1 text-center ${A.title}`}>{t("bankAccounts.editTitle")}</h2>

        <button
          onClick={() =>
            navigate(companyRoute(company_id, "/settings/accounts"))
          }
          className={`${A.iconBtn} inline-flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          aria-label={t("directory.common.close")}
          type="button"
        >
          <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Card */}
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md px-2"
        autoComplete="off"
      >
        {/* ✅ keep overflow-visible so AppleComboBox dropdown can render */}
        <div className={`${A.card} p-6 overflow-visible`}>
          <div className="mb-5">
            <label className={labelCls}>{t("directory.common.name")}</label>
            <input
              type="text"
              className={A.inputBase}
              name="account_name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
              autoFocus
              maxLength={40}
              disabled={saving}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
            />
          </div>

          <div className="mb-5 flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-3 ring-1 ring-black/5">
            <div className={A.label}>{t("generalLedger.balance")}</div>
            <div className={`${A.body} ${A.num} font-medium`}>
              {balanceText()}
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
              {error.kind === "server" ? error.detail : t("purchases.errors.saveChanges")}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            className="w-full justify-center"
            disabled={saving}
          >
            {saving ? t("directory.common.savingDots") : t("bankAccounts.saveChanges")}
          </Button>
        </div>
      </form>
    </div>
  );
}
